import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { money } from '@/lib/utils';
import { PageHero } from './shared';
import { Products } from './Home';

interface PublicProduct {
  id: string;
  name: string;
  description: string | null;
  minimumAmount: string;
  maximumAmount: string;
  minimumInstallments: number;
  maximumInstallments: number;
  repaymentFrequency: string;
  requiredGuarantors: number;
}

/**
 * Loan products, in full.
 *
 * The home page shows the same cards; this page adds the comparison table,
 * which is what someone deciding between products actually wants and which is
 * too dense for a landing page.
 */
export default function Loans() {
  const { data: products = [] } = useQuery({
    queryKey: ['public-products'],
    queryFn: () => api.get<PublicProduct[]>('/loan-products/public'),
  });

  return (
    <>
      <PageHero
        eyebrow="Loan products"
        title="Every one of them interest-free."
        description="They differ in how much you can borrow, how long you have to repay it and how many guarantors you need. None of them differ in cost, because none of them have one."
      />

      <Products products={products} />

      {products.length > 0 && (
        <section className="py-14 md:py-20">
          <div className="container">
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <p className="eyebrow mb-3">Side by side</p>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                Compare the products.
              </h2>
            </div>

            {/* Scrolls inside itself on a narrow screen; the page never does. */}
            <div className="mx-auto max-w-4xl overflow-x-auto rounded-2xl border border-border bg-card">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left">
                    <th className="px-5 py-3.5 font-black uppercase tracking-wider text-foreground-secondary sm:px-6">
                      Product
                    </th>
                    <th className="px-5 py-3.5 text-right font-black uppercase tracking-wider text-foreground-secondary sm:px-6">
                      Up to
                    </th>
                    <th className="px-5 py-3.5 text-right font-black uppercase tracking-wider text-foreground-secondary sm:px-6">
                      Repayments
                    </th>
                    <th className="px-5 py-3.5 text-right font-black uppercase tracking-wider text-foreground-secondary sm:px-6">
                      Guarantors
                    </th>
                    <th className="px-5 py-3.5 text-right font-black uppercase tracking-wider text-foreground-secondary sm:px-6">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="border-b border-border last:border-b-0">
                      <td className="px-5 py-4 font-bold text-foreground sm:px-6">
                        {product.name}
                      </td>
                      <td className="numeric px-5 py-4 text-right font-semibold text-foreground sm:px-6">
                        {money(product.maximumAmount, true)}
                      </td>
                      <td className="numeric px-5 py-4 text-right text-foreground-secondary sm:px-6">
                        {product.minimumInstallments}&ndash;{product.maximumInstallments}{' '}
                        {product.repaymentFrequency.toLowerCase()}
                      </td>
                      <td className="numeric px-5 py-4 text-right text-foreground-secondary sm:px-6">
                        {product.requiredGuarantors}
                      </td>
                      <td className="px-5 py-4 text-right sm:px-6">
                        <span className="inline-flex items-center gap-1.5 font-bold text-success">
                          <BadgeCheck size={14} /> None
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mx-auto mt-8 flex max-w-4xl gap-3.5 rounded-2xl border border-border bg-background-alt p-5">
              <Info size={18} className="mt-0.5 shrink-0 text-foreground-secondary" />
              <p className="text-sm leading-relaxed text-foreground-secondary">
                A reviewer may approve less than you ask for, or a different repayment period. The
                limits above are the ceiling for each product, not a guarantee of what any
                individual application will be granted.
              </p>
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-7 py-4 font-bold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5"
              >
                Apply for a loan <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
